import type { ExplorerFinding } from '../../../../models/explorer/ExplorerFinding';
import type { FindingRule, FindingRuleContext } from '../types';
import { azList, isOneOfService } from './_graphHelpers';

const HA_SENSITIVE_SERVICES = [
  'rds',
  'db-instance',
  'db-cluster',
  'ec2',
  'instance',
  'lambda',
  'elasticache',
  'alb',
  'nlb',
] as const;

/**
 * P0.3 — single-AZ resource (HA risk).
 * Severity: warn. SOC 2 A1.2 / ISO 27001 A.17.
 *
 * Fires for compute/data/ingress resources with exactly one AZ. Multi-
 * AZ is the recommended deployment shape; single-AZ is a latent risk.
 */
export const singleAzRule: FindingRule = {
  id: 'single_az_resource',
  label: 'Single-AZ resource',
  description: 'HA-sensitive resource deployed in only one availability zone.',

  run(ctx: FindingRuleContext): readonly ExplorerFinding[] {
    const findings: ExplorerFinding[] = [];
    for (const node of ctx.graph.nodes) {
      if (!isOneOfService(node, HA_SENSITIVE_SERVICES)) continue;
      const azs = azList(node);
      if (azs.length === 1) {
        findings.push({
          id: `single_az_resource:resource:${node.id}`,
          ruleId: 'single_az_resource',
          severity: 'warn',
          scope: 'resource',
          subject: { kind: 'resource', id: node.id },
          title: 'Single-AZ resource',
          detail: `${String(node.label ?? node.id)} is deployed in only one AZ (${azs[0]}).`,
          attrs: { az: azs[0] },
        });
      }
    }
    return findings;
  },
};
